import { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";

import { DispensaryV3 } from "custom-types/DispensariesV3";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import { getDispensaryUrl } from "utils/dispensaryUrls";
import { getDistance } from "utils/distance";

import KeyDeliveryVariables from "components/botanic/KeyDeliveryVariables";
import ScheduleStatus from "components/botanic/ScheduleStatus";
import StarRating from "components/botanic/StarRating";
import Image from "components/Image";
import TrackImpression from "components/TrackImpression";

const DeliveryCarouselHeader = ({
  dispensary,
  isDeliveryAvailable,
  merchandisingCampaignId,
}: {
  dispensary: DispensaryV3;
  isDeliveryAvailable: boolean;
  merchandisingCampaignId?: number;
}) => {
  const countryCode = useDomainCountryCode();
  const overflowRef = useRef<HTMLDivElement>(null);
  const distanceAway = getDistance(dispensary.distanceMi, countryCode);
  const [isBlur, setIsBlur] = useState(false);

  const displaySchedule = useMemo(
    () =>
      // If this is the delivery view of the carousel, only display the dispensary schedule information
      // if the dispensary has set up a delivery schedule.
      // If this is the pickup view of the carousel, only display the dispensary schedule information
      // if the dispensary has set up a pickup schedule.
      isDeliveryAvailable
        ? !!dispensary.currentStatuses?.delivery?.statusChangeAt
        : !!dispensary.currentStatuses?.pickup?.statusChangeAt,
    [dispensary],
  );

  useEffect(() => {
    const overflowDiv = overflowRef.current;
    if (overflowDiv) {
      setIsBlur(overflowDiv.scrollWidth > overflowDiv.offsetWidth);
      const updateBlur = () => {
        setIsBlur(overflowDiv.scrollWidth > overflowDiv.offsetWidth);
      };
      window.addEventListener("resize", updateBlur);

      return () => {
        window.removeEventListener("resize", updateBlur);
      };
    }
  }, []);
  const { publishEvent } = useEventTracker();
  return (
    <>
      <TrackImpression
        trackingFunction={() =>
          publishEvent({
            action: "impression",
            category: "Delivery Gate Flow",
            dispensaryId: dispensary.id,
            label: "retailer header link",
            merchandisingCampaignId: merchandisingCampaignId,
          })
        }
      >
        <div className="text-xs font-normal flex mt-lg">
          {!!dispensary.logoUrl && (
            <div
              style={{
                height: "48px",
                width: "48px",
              }}
              className="rounded overflow-hidden"
            >
              <Image
                sizes={[48]}
                src={dispensary.logoUrl}
                alt={`Logo for ${dispensary.name}`}
              />
            </div>
          )}
          <div className="ml-md overflow-hidden flex flex-col items-start">
            <div className="text-sm font-bold underline">
              <a
                href={getDispensaryUrl({
                  countryCode,
                  slug: dispensary.slug,
                })}
                onClick={() =>
                  publishEvent({
                    action: "click",
                    category: "Delivery Gate Flow",
                    dispensaryId: dispensary.id,
                    label: "retailer header link",
                    merchandisingCampaignId: merchandisingCampaignId,
                  })
                }
              >
                {dispensary.name}
              </a>
            </div>
            <div
              ref={overflowRef}
              className={classNames("w-full", {
                "overflow-blur": isBlur,
              })}
            >
              <div className="flex flex-wrap flex-row mt-xs">
                {displaySchedule && (
                  <>
                    <div className="whitespace-nowrap">
                      <ScheduleStatus
                        currentStatus={
                          isDeliveryAvailable
                            ? dispensary.currentStatuses?.delivery
                            : dispensary.currentStatuses?.pickup
                        }
                        scheduleType={
                          isDeliveryAvailable ? "delivery" : "pickup"
                        }
                        timeZone={dispensary.timeZone}
                      />
                    </div>
                    {!!dispensary.starRating && (
                      <span aria-hidden className="mx-sm">
                        •
                      </span>
                    )}
                  </>
                )}
                <StarRating
                  rating={dispensary.starRating}
                  ratingCount={dispensary.reviewCount}
                  starWidth={13}
                />
              </div>
            </div>
            {isDeliveryAvailable ? (
              <div className="bg-leafly-white p-xs rounded w-auto overflow-hidden">
                <KeyDeliveryVariables
                  deliveryServiceAreaRanges={
                    dispensary.deliveryServiceAreaRanges || {
                      maxDeliveryDurationMaximum: null,
                      maxFee: null,
                      maxOrderMinimum: null,
                    }
                  }
                  deliveryServiceAreaInfo={
                    dispensary.exactDeliveryServiceArea || undefined
                  }
                />
              </div>
            ) : (
              <span>
                {dispensary.distanceMi
                  ? `${distanceAway} away - ${dispensary.city}, ${dispensary.state}`
                  : "Pickup available"}
              </span>
            )}
          </div>
        </div>
      </TrackImpression>
      <style jsx>{`
        .overflow-blur {
          overflow: hidden;
          position: relative;
          white-space: nowrap;
        }
        .overflow-blur::after {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          width: 30px;
          height: 100%;
          background: linear-gradient(
            to right,
            rgba(255, 255, 255, 0),
            rgba(255, 255, 255, 1)
          );
        }
      `}</style>
    </>
  );
};

export default DeliveryCarouselHeader;
